// Banco de pruebas (página /actions): reset de teléfonos de prueba e inyección
// de leads como si vinieran de un portal.
// Backend: /api/v1/leads/test-bench/{phones,reset,inject-lead}/

import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'

const BASE = '/api/v1/leads/test-bench'

export type TestBenchPortalReset = {
  ok: boolean
  error?: string
  demandConsents?: number
  interestRegistrations?: number
}

export type TestBenchResetResult = {
  phone: string
  executions: number
  suppressions: number
  users: number
  conversations: number
  messages: number
  meetings: number
  portal: TestBenchPortalReset
}

export type TestBenchInjectInput = {
  phone: string
  id_inmueble: number
  agente_gestor: number
  nombre?: string
  apellidos?: string
  email?: string
  portal?: 'idealista' | 'fotocasa' | 'pisos.com' | 'habitaclia'
  notas?: string
}

export type TestBenchInjectResult = {
  row: { IdLead: number; IdInmueble: number; Movil: string; AgenteGestor: number }
  result: {
    status: string
    error?: string
    whatsapp_template?: { status?: string; skipped?: boolean; error?: string | null; dedupe_key?: string }
    rk_demand?: { ok: boolean; skipped?: boolean; reason?: string; error?: string; status_code?: number }
    lead_processor?: { status?: string; user_id?: string }
  }
}

class TestBenchServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = getConversationsAuthToken()
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Token ${token}` } : {}),
        ...options.headers,
      },
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      const detail =
        (payload as { error?: string }).error ||
        (payload as { detail?: string }).detail ||
        JSON.stringify(payload) ||
        response.statusText
      throw new Error(detail || `HTTP ${response.status}`)
    }

    return response.json() as Promise<T>
  }

  getPhones() {
    return this.request<{ phones: string[] }>(`${BASE}/phones/`)
  }

  savePhones(phones: string[]) {
    return this.request<{ phones: string[] }>(`${BASE}/phones/`, {
      method: 'PUT',
      body: JSON.stringify({ phones }),
    })
  }

  resetPhone(phone: string) {
    return this.request<TestBenchResetResult>(`${BASE}/reset/`, {
      method: 'POST',
      body: JSON.stringify({ phone }),
    })
  }

  injectLead(input: TestBenchInjectInput) {
    return this.request<TestBenchInjectResult>(`${BASE}/inject-lead/`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }
}

export const testBenchService = new TestBenchServiceClient()
